import sumBy from 'lodash/sumBy';

import { GTM_CONTAINER_ID } from 'src/config/constants';
import { type ListingProduct, ListingProductCode } from 'src/generated/graphql';
import type { UserRegistrationType } from 'src/types/userRegistrationType';

export const firePaymentSuccessEvent = (
  products: ListingProduct[],
): boolean => {
  const eventName = 'paymentSuccess';
  // Fire a custom analytics event to GTM and HEAP
  // This assumes that HEAP and GTM are loaded in the html
  // Fire HEAP Custom Event
  const heapEvent = {
    value: sumBy(products, 'cost') / 100,
    products: products.map((p) => p.productCode).join(','), // heap does not understand arrays of objects, so this needs to be flattened
  };
  window.heap.track(eventName, heapEvent);

  // Tracks only core listing products purchased (i.e 6month, LUS, PayPerEnquiry)
  // to be used for `/private-seller-info` experiment
  products.forEach((product) => {
    fireListingCoreProductPurchasesToHeap(product.productCode);
  });

  // Adroll payment success event tracking
  window.__adroll?.record_user({ adroll_segments: '25ec50e5' });

  // GTM has a different format for events. Need to include the
  // event name as a property
  const googleEvent = {
    event: eventName,
    value: sumBy(products, 'cost') / 100,
    products,
  };

  // Fire GTM Custom Event
  window.dataLayer = window.dataLayer || []; // from https://www.analyticsmania.com/post/google-tag-manager-custom-event-trigger/
  window.dataLayer.push(googleEvent);

  return true;
};

export const fireOnboardingCompleteEvent = async (
  registrationType: UserRegistrationType,
): Promise<boolean> =>
  new Promise((resolve) => {
    const eventName = 'onboardingComplete';

    const heapEvent = {
      registrationType,
    };
    fireHeapEvent(eventName, heapEvent);

    const googleEvent = {
      event: eventName,
      ...heapEvent,
      eventCallback: (gtmId: string) => googleEventCallback(resolve, gtmId),
    };

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(googleEvent);

    // This acts as a safeguard, if googleEvent.eventCallback is unreliable
    // Which forces a promise resolution if googleEvent.eventCallback is unresponsive
    setTimeout(() => {
      resolve(true);
    }, 2000);

    return true;
  });

export const fireOnboardingCompleteSemEvent = async (
  registrationType: UserRegistrationType,
): Promise<boolean> =>
  new Promise((resolve) => {
    const semEventName = 'onboardingCompleteSem';
    const semGoogleEvent = {
      event: semEventName,
      registrationType,
      eventCallback: (gtmId: string) => googleEventCallback(resolve, gtmId),
    };
    window.dataLayer.push(semGoogleEvent);

    // This acts as a safeguard, if googleEvent.eventCallback is unreliable
    // Which forces a promise resolution if googleEvent.eventCallback is unresponsive
    setTimeout(() => {
      resolve(true);
    }, 2000);

    return true;
  });

export const fireRecommendationFeedbackEvent = (
  relevant: boolean,
  buyerId: string,
  listingId: string,
) => {
  const eventName = `recommendationRelevant${relevant ? 'Yes' : 'No'}`;
  const heapEvent = {
    buyerId,
    listingId,
  };
  window.heap.track(eventName, heapEvent);
};

export const fireRegistrationEvent = async (
  onboardPageSource: string | 'Sem',
  registrationStatus?: 'Start' | 'Complete',
): Promise<boolean> =>
  new Promise((resolve) => {
    const eventName = `registration${registrationStatus}${onboardPageSource}`;

    const googleEvent = {
      event: eventName,
      eventCallback: (gtmId: string) => googleEventCallback(resolve, gtmId),
    };

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(googleEvent);

    // This acts as a safeguard, if googleEvent.eventCallback is unreliable
    // Which forces a promise resolution if googleEvent.eventCallback is unresponsive
    setTimeout(() => {
      resolve(true);
    }, 2000);

    return true;
  });

export const fireHeapEvent = (eventName: string, event: Record<any, any>) => {
  window.heap.track(eventName, event);
};

export const googleEventCallback = (
  resolve: (resolvedValue: boolean) => void,
  gtmId: string,
) => {
  if (gtmId === GTM_CONTAINER_ID) {
    resolve(true);
  }
};

export const fireListingCoreProductPurchasesToHeap = (productCode: string) => {
  switch (productCode) {
    case ListingProductCode.SixAdPack: {
      window.heap.track('listingCoreProductPurchased', {
        productCode: ListingProductCode.SixAdPack,
      });
      return;
    }
    case ListingProductCode.SixAdPackAndSearchBoost: {
      window.heap.track('listingCoreProductPurchased', {
        productCode: ListingProductCode.SixAdPack,
      });
      return;
    }
    case ListingProductCode.ListUntilSold: {
      window.heap.track('listingCoreProductPurchased', {
        productCode: ListingProductCode.ListUntilSold,
      });
      return;
    }
    case ListingProductCode.ListUntilSoldAndSearchBoostLus: {
      window.heap.track('listingCoreProductPurchased', {
        productCode: ListingProductCode.ListUntilSold,
      });
      return;
    }
    case ListingProductCode.PayPerEnquiry: {
      window.heap.track('listingCoreProductPurchased', {
        productCode: ListingProductCode.PayPerEnquiry,
      });
      return;
    }
    case ListingProductCode.Freemium: {
      window.heap.track('listingCoreProductPurchased', {
        productCode: ListingProductCode.Freemium,
      });
      return;
    }
    case ListingProductCode.Accelerate: {
      window.heap.track('listingCoreProductPurchased', {
        productCode: ListingProductCode.Accelerate,
      });
      return;
    }
    default: {
      // Don't need to track anything else
      return;
    }
  }
};
